import type { AxiosError } from "axios";
import type { UseMutationOptions } from "@tanstack/react-query";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { get, post } from "../request-helpers";
import type {
  GetAiChatCapabilities,
  GetAiChatThread,
  PostAiFeedback,
  PostGenerateNotificationAI,
  PostGenerateShiftsAI,
  PostTranslateNotificationAI,
} from "@/server-types";

type PostGenerateShiftsAIVariables = {
  body: PostGenerateShiftsAI["body"];
};

type MutationOpts = Omit<
  UseMutationOptions<
    PostGenerateShiftsAI["response"],
    AxiosError,
    PostGenerateShiftsAIVariables
  >,
  "mutationFn" | "onError"
> & {
  displayNotification?: boolean;
  onError?: (
    error: AxiosError,
    variables: PostGenerateShiftsAIVariables,
    context: unknown,
    processedError: string | null,
    processedErrorCode: string | null,
  ) => void;
};

export const usePostGenerateShiftsAI = (options?: MutationOpts) => {
  return useCustomMutation({
    mutationFn: (props: PostGenerateShiftsAIVariables) =>
      post<PostGenerateShiftsAI>(`/ai/shifts/generate`, props.body),
    ...options,
  });
};

type PostTranslateNotificationAIVariables = {
  body: PostTranslateNotificationAI["body"];
};

type TranslateMutationOpts = Omit<
  UseMutationOptions<
    PostTranslateNotificationAI["response"],
    AxiosError,
    PostTranslateNotificationAIVariables
  >,
  "mutationFn" | "onError"
> & {
  displayNotification?: boolean;
  onError?: (
    error: AxiosError,
    variables: PostTranslateNotificationAIVariables,
    context: unknown,
    processedError: string | null,
    processedErrorCode: string | null,
  ) => void;
};

export const usePostTranslateNotificationAI = (
  options?: TranslateMutationOpts,
) => {
  return useCustomMutation({
    mutationFn: (props: PostTranslateNotificationAIVariables) =>
      post<PostTranslateNotificationAI>(
        `/ai/notifications/translate`,
        props.body,
      ),
    ...options,
  });
};

type PostGenerateNotificationAIVariables = {
  body: PostGenerateNotificationAI["body"];
};

type GenerateNotificationMutationOpts = Omit<
  UseMutationOptions<
    PostGenerateNotificationAI["response"],
    AxiosError,
    PostGenerateNotificationAIVariables
  >,
  "mutationFn" | "onError"
> & {
  displayNotification?: boolean;
  onError?: (
    error: AxiosError,
    variables: PostGenerateNotificationAIVariables,
    context: unknown,
    processedError: string | null,
    processedErrorCode: string | null,
  ) => void;
};

export const usePostGenerateNotificationAI = (
  options?: GenerateNotificationMutationOpts,
) => {
  return useCustomMutation({
    mutationFn: (props: PostGenerateNotificationAIVariables) =>
      post<PostGenerateNotificationAI>(
        `/ai/notifications/generate`,
        props.body,
      ),
    ...options,
  });
};

type PostAiFeedbackVariables = {
  body: PostAiFeedback["body"];
};

type FeedbackMutationOpts = Omit<
  UseMutationOptions<
    PostAiFeedback["response"],
    AxiosError,
    PostAiFeedbackVariables
  >,
  "mutationFn" | "onError"
> & {
  displayNotification?: boolean;
  onError?: (
    error: AxiosError,
    variables: PostAiFeedbackVariables,
    context: unknown,
    processedError: string | null,
    processedErrorCode: string | null,
  ) => void;
};

export const usePostAiFeedback = (options?: FeedbackMutationOpts) => {
  return useCustomMutation({
    mutationFn: (props: PostAiFeedbackVariables) =>
      post<PostAiFeedback>(`/ai/feedback`, props.body),
    ...options,
  });
};

/**
 * Restores the panel's conversation after a reload. The stored transcript
 * would otherwise be write-only: the panel comes back empty and a pending
 * approval, which lives in the message parts, is lost with the tab.
 */
export const useGetAiChatThread = (
  params: { id: string },
  options?: { enabled?: boolean },
) => {
  return useCustomQuery({
    queryKey: ["ai", "chat", "thread", params.id],
    queryFn: () => get<GetAiChatThread>(`/ai/chat/thread`, { id: params.id }),
    // One restore per page load. Refetching would fight whatever the user has
    // typed or streamed since.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
    enabled: options?.enabled ?? true,
  });
};

/**
 * What the assistant can actually do for this caller. Derived server-side from
 * the same tool registry the model is handed, so the list cannot drift from the
 * real capabilities.
 */
export const useGetAiChatCapabilities = (options?: { enabled?: boolean }) => {
  return useCustomQuery({
    queryKey: ["ai", "chat", "capabilities"],
    queryFn: () => get<GetAiChatCapabilities>(`/ai/chat/capabilities`),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
};
